/**
 * UI-2a: who last unlocked this iPad, so its lock screen offers them first
 * (the owner, 19 Oct: the last three, then everyone). The server lists every
 * active person on every device, however many there are; the counter's own
 * few are the ones who unlocked it last.
 *
 * User ids only: never a name, never a PIN. Names come from the server's list
 * every time, so a person taken off the device leaves the row with it.
 */
const RECENT = 'rental.staff.lock-recent'

/** The row above the PIN: one line of tiles, at most. */
export const ROW = 3

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The ids this iPad remembers, the last to unlock first; none when storage says nothing or refuses. */
export function readRecent(storage: Pick<Storage, 'getItem'> | null = localStore()): string[] {
  try {
    const value: unknown = JSON.parse(storage?.getItem(RECENT) ?? '[]')
    if (!Array.isArray(value)) return []
    return value.filter((id): id is string => typeof id === 'string' && UUID.test(id)).slice(0, ROW)
  } catch {
    return []
  }
}

/** Someone unlocked here: first in the memory, which keeps the last three. */
export function rememberUnlock(
  userId: string,
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = localStore(),
) {
  if (!UUID.test(userId)) return
  const next = [userId, ...readRecent(storage).filter((id) => id !== userId)].slice(0, ROW)
  try {
    storage?.setItem(RECENT, JSON.stringify(next))
  } catch {
    // Storage refused: the lock screen offers everyone, as on a new iPad.
  }
}

/**
 * The tiles above the PIN, in the server's order (by name):
 * - everyone, when there are three people or fewer;
 * - otherwise the last three here, with whoever is chosen taking the place of
 *   the least recent when they are not among them.
 * Empty when this iPad remembers nobody still on the list: everyone is shown.
 */
export function lockRow<Person extends { id: string }>(
  people: readonly Person[],
  recent: readonly string[],
  chosen: string | null = null,
): Person[] {
  if (people.length <= ROW) return [...people]
  const listed = new Set(people.map((person) => person.id))
  let ids = recent.filter((id) => listed.has(id))
  if (chosen !== null && listed.has(chosen) && !ids.includes(chosen)) ids = [chosen, ...ids]
  const shown = new Set(ids.slice(0, ROW))
  return people.filter((person) => shown.has(person.id))
}

function localStore(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}
